<script lang="ts" setup>
import AnimationIcon from '@/components/common/AnimationIcon.vue';
import { useResgate } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { newApi } from '@/newApi';
import { onMounted, onUnmounted, ref } from 'vue';

const props = defineProps({
    presetUsername: {
        type: String,
        required: true
    }
});

const loginStore = useLoginStore();
const resgate = useResgate(crypto.randomUUID());

const username = ref(props.presetUsername);

const forgotPasswordSubmitSucceded = ref(false);

async function sendForgotPasswordEmail(): Promise<void> {
    forgotPasswordSubmitSucceded.value = false;
    await newApi.user.sendForgotPasswordMail(resgate, username.value.trim()).finally(() => {
        forgotPasswordSubmitSucceded.value = true;
    });
}

onMounted(() => {
    forgotPasswordSubmitSucceded.value = false;
});

onUnmounted(() => {
    resgate.unsubscribe();
});
</script>

<template>
    <SText
        is="p"
        size="2"
        class="mb-8"
    >
        {{ $t('system.resetPassword') }}
    </SText>
    <template v-if="forgotPasswordSubmitSucceded === false">
        <SFormGroup id="password" :label="$t('components.baseRegistry.username')" class="mb-7">
            <SFormInput v-model="username" type="text" />
        </SFormGroup>

        <SStack vertical justify-content="between">
            <SButton
                variant="primary"
                class="mb-5"
                block
                :disabled="username.trim().length === 0"
                @click="sendForgotPasswordEmail"
            >
                {{ $t('components.login.sendLinkToEmail') }}
            </SButton>
            <SButton
                class="mb-7"
                type="clear"
                @click="loginStore.setForgotPassword(false)"
            >
                {{ $t('system.cancel') }}
            </SButton>
        </SStack>
    </template>
    <SStack v-if="forgotPasswordSubmitSucceded === true" vertical>
        <SStack class="mb-5">
            <AnimationIcon
                :toggled="true"
                icon="checkmark-green"
                size="xxlarge"
                :speed="1.5"
            />
        </SStack>
        <SText size="3" weight="bold" class="mb-3">
            {{ $t('components.login.emailSent') }}
        </SText>
        <SText size="4" class="mb-10">
            {{ $t('components.login.emailSentDescription') }}
        </SText>
        <SButton
            class="mb-7 sy-type-blue"
            @click="loginStore.setForgotPassword(false)"
        >
            {{ $t('components.login.backToLogin') }}
        </SButton>
    </SStack>
</template>
